// src/components/access/UsersTable.jsx

import React, { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";

import { Edit2, PlusCircle, Search, X, Trash2, MailOpen, RefreshCcw, Send
} from "lucide-react";
import UpsertUserModal from "@/components/admin/access-control/UpsertUserModal";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { usePermission } from "@/contexts/RoleAccessContext";
import { useAuth } from "@/contexts/AuthContext";
import { resendInviteService } from "@/services/adminService";
import { getErrorMessage } from "@/utils/errorHandler";
import { toast } from "sonner";
import ManagedEmployeesWarning from "./ManagedEmployeesWarning";

function normalizeStatus(value) {
  const v = (value || "").toUpperCase();
  if (v === "active" || v === "inactive" || v === "invited") return v;
  return "invited";
}

function getStatusBadge(status) {
  if (status.toLowerCase() === "active") {
    return (
      <Badge className="bg-emerald-500/15 text-emerald-600 border border-emerald-500/25 dark:text-emerald-400 hover:bg-emerald-500/25">
        Active
      </Badge>
    );
  }
  if (status.toLowerCase() === "inactive") {
    return (
      <Badge
        variant="outline"
        className="bg-slate-500/20 border-slate-300 text-slate-600 dark:border-slate-700 dark:text-slate-300 hover:bg-slate-500/15"
      >
        Inactive
      </Badge>
    );
  }
  return (
    <Badge className="bg-amber-500/15 text-amber-700 border border-amber-500/25 dark:text-amber-300 hover:bg-amber-500/25">
      Invited
    </Badge>
  );
}

export default function UsersTable({
  users = [],
  roles = [],
  rolesById,
  loading,
  onUpsertUser,
  onDeleteUser,
}) {
  const [query, setQuery] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  // ✅ Delete modal state
  const [deleteUser, setDeleteUser] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [resendingUserId, setResendingUserId] = useState(null);

  const { can } = usePermission();
  const { user: currentUser } = useAuth();
  const canViewUsers = can("roles_and_access", "view");

  // The Default Admin (the company owner's login) is changed only by them, and
  // never removed by someone else.
  const isDefaultAdminRow = (u) =>
    Boolean(u?.access_role_id && rolesById?.get(String(u.access_role_id))?.is_default);
  const isMe = (u) => String(u?._id ?? u?.id) === String(currentUser?.id);
  const lockedForMe = (u) => isDefaultAdminRow(u) && !isMe(u);
  const canCreateUser = can("roles_and_access", "create");
  const canEditUser = can("roles_and_access", "edit");
  const canDeleteUserPermission = can("roles_and_access", "delete");

  const filteredUsers = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;

    return users.filter((u) => {
      const name = (u.name || "").toLowerCase();
      const email = (u.email || "").toLowerCase();
      const roleName =
        rolesById?.get(String(u.access_role_id || ""))?.name?.toLowerCase() ||
        "";
      return name.includes(q) || email.includes(q) || roleName.includes(q);
    });
  }, [query, users, rolesById]);

  const openInvite = () => {
    if (!canCreateUser) return;
    setEditingUser(null);
    setModalOpen(true);
  };

  const openEdit = (u) => {
    if (!canEditUser) return;
    setEditingUser(u);
    setModalOpen(true);
  };

  const openDelete = (u) => {
    if (!canDeleteUserPermission) return;
    setDeleteUser(u);
  };

  const handleDelete = async () => {
    if (!canDeleteUserPermission) return;
    if (!deleteUser || !onDeleteUser) return;

    try {
      setDeleting(true);
      await onDeleteUser(deleteUser);
      setDeleteUser(null);
    }
    finally {
      setDeleting(false);
    }
  };

  const clearSearch = () => setQuery("");

  const handleResendInvite = async (userId) => {
    try {
      setResendingUserId(userId);

      await resendInviteService(userId);

      toast.success("Invitation resent successfully");
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to resend invitation"));
    } finally {
      setResendingUserId(null);
    }
  };

  function TableSkeletonRow({ i }) {
    return (
      <tr key={i} className="border-b last:border-b-0">
        <td className="px-4 py-4">
          <div className="h-4 w-40 rounded bg-slate-200/70 dark:bg-slate-800/60" />
        </td>
        <td className="px-4 py-4">
          <div className="h-4 w-56 rounded bg-slate-200/70 dark:bg-slate-800/60" />
        </td>
        <td className="px-4 py-4">
          <div className="h-4 w-28 rounded bg-slate-200/70 dark:bg-slate-800/60" />
        </td>
        <td className="px-4 py-4">
          <div className="h-6 w-20 rounded bg-slate-200/70 dark:bg-slate-800/60" />
        </td>
        <td className="px-4 py-4">
          <div className="h-8 w-20 rounded bg-slate-200/70 dark:bg-slate-800/60" />
        </td>
      </tr>
    );
  }
  
  if (!canViewUsers) {
    return (
      <div className="p-10 text-center text-muted-foreground">
        <p className="font-semibold">Access Restricted</p>
        <p className="text-sm">You do not have permission to view Users.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Top bar */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div className="space-y-0.5">
            <h2 className="text-lg font-semibold text-foreground">Users</h2>
            <p className="text-xs text-muted-foreground">
              Invite users, assign roles, and manage access.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <div className="relative w-full sm:w-[320px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search users..."
                className="pl-9 pr-9"
              />
              {query?.trim() ? (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-muted text-muted-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              ) : null}
            </div>

            <Button disabled={!canCreateUser} onClick={openInvite} className="gap-2">
              <PlusCircle className="h-4 w-4" />
              Invite User
            </Button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="border dark:border-gray-700 rounded-lg overflow-hidden bg-white/60 dark:bg-slate-950/30">
        <div className="w-full overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 border-b dark:border-gray-700">
              <tr className="text-left">
                <th className="px-4 py-3 font-semibold text-slate-700 dark:text-slate-200">
                  Name
                </th>
                <th className="px-4 py-3 font-semibold text-slate-700 dark:text-slate-200">
                  Email ID
                </th>
                <th className="px-4 py-3 font-semibold text-slate-700 dark:text-slate-200">
                  Role
                </th>
                <th className="px-4 py-3 font-semibold text-slate-700 dark:text-slate-200">
                  Status
                </th>
                <th className="px-4 py-3 font-semibold text-slate-700 dark:text-slate-200 w-[110px]">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <TableSkeletonRow key={i} i={i} />
                ))
              ) : filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center">
                    <div className="space-y-2">
                      <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                        No users found
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Try a different search, or invite a new user.
                      </p>
                      <Button disabled={!canCreateUser} size="sm" className="gap-2" onClick={openInvite}>
                        <PlusCircle className="h-4 w-4" />
                        Invite User
                      </Button>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredUsers.map((u) => {
                  const id = String(u._id ?? u.id ?? u.email);
                  const roleName =
                    (u.access_role_id &&
                      rolesById?.get(String(u.access_role_id))?.name) ||
                    "—";
                  const status = normalizeStatus(u.status);

                  return (
                    <tr
                      key={id}
                      className="border-b last:border-b-0 dark:border-gray-700 hover:bg-muted/30 transition-colors"
                    >
                      <td className="px-4 py-4">
                        <div className="flex flex-col">
                          <span className="font-semibold text-slate-900 dark:text-slate-100">
                            {u.name?.trim() ? u.name : "—"}
                          </span>
                          {u.linked_employee && (
                            <span className="text-xs text-muted-foreground">
                              Also employee {u.linked_employee.employee_id}
                            </span>
                          )}
                          <span className="text-xs text-muted-foreground md:hidden">
                            {u.email}
                          </span>
                        </div>
                      </td>

                      <td className="px-4 py-4 text-slate-700 dark:text-slate-200 hidden md:table-cell">
                        {u.email}
                      </td>

                      <td className="px-4 py-4 text-slate-700 dark:text-slate-200">
                        <span className="inline-flex items-center gap-2">
                          <span className="h-2 w-2 rounded-full bg-primary/70" />
                          {roleName}
                        </span>
                      </td>

                      <td className="px-4 py-4">{getStatusBadge(u.status)}</td>

                      <td className="px-4 py-3 flex items-center justify-end gap-1.5">
                        {u.status === "invited" && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleResendInvite(u._id)}
                            disabled={resendingUserId === u._id}
                            className={`
        h-8 w-8 transition-all duration-300 relative
        ${
          resendingUserId === u._id
            ? "bg-primary/10 text-primary animate-pulse shadow-[0_0_10px_rgba(var(--primary),0.2)]"
            : "text-muted-foreground hover:text-primary hover:bg-primary/10"
        }
      `}
                            title="Resend Invitation"
                          >
                            {resendingUserId === u._id ? (
                              <RefreshCcw
                                size={14}
                                strokeWidth={2.5}
                                className="animate-spin"
                              />
                            ) : (
                              <Send size={14} strokeWidth={2.2} />
                            )}
                          </Button>
                        )}

                        {/* Edit Button */}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openEdit(u)}
                          disabled={!canEditUser || lockedForMe(u)}
                          title={
                            lockedForMe(u)
                              ? "Only the Default Admin can change their own details"
                              : "Edit"
                          }
                          className="h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                        >
                          <Edit2 size={15} strokeWidth={2.2} />
                        </Button>

                        {/* Delete Button */}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openDelete(u)}
                          disabled={!canDeleteUserPermission || isDefaultAdminRow(u)}
                          title={isDefaultAdminRow(u) ? "The Default Admin can't be removed" : "Delete"}
                          className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                        >
                          <Trash2 size={15} strokeWidth={2.2} />
                        </Button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Footer hint */}
        {!loading && filteredUsers.length > 0 ? (
          <div className="px-4 py-3 border-t dark:border-gray-700 text-xs text-muted-foreground flex items-center justify-between">
            <span>Tip: Search works by name, email, and role.</span>
            <span className="hidden sm:inline">
              Total Users: {Array.isArray(filteredUsers) ? filteredUsers.length : 0}
            </span>
          </div>
        ) : null}
      </div>

      {/* Invite/Edit Modal */}
      <UpsertUserModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        roles={roles}
        editingUser={editingUser}
        onSave={onUpsertUser}
        canGrantExisting={canEditUser}
      />

      {/* ✅ Delete Confirmation Modal */}
      <Dialog open={!!deleteUser} onOpenChange={() => setDeleteUser(null)}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>
              {deleteUser?.linked_employee ? "Remove Admin Access" : "Delete User"}
            </DialogTitle>
          </DialogHeader>

          {deleteUser?.linked_employee ? (
            <p className="text-sm text-muted-foreground">
              <span className="font-semibold">
                {deleteUser?.name || deleteUser?.email}
              </span>{" "}
              is also employee {deleteUser.linked_employee.employee_id}. They'll
              lose admin access but keep their login for their employee
              workspace.
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              Are you sure you want to delete{" "}
              <span className="font-semibold">
                {deleteUser?.name || deleteUser?.email}
              </span>
              ? This action cannot be undone.
            </p>
          )}

          {/* Removing a linked admin's access keeps their team (any login can manage). */}
          {!deleteUser?.linked_employee && (
            <ManagedEmployeesWarning user={deleteUser} action="delete" />
          )}

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setDeleteUser(null)}>
              Cancel
            </Button>

            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleteUser?.linked_employee
                ? deleting
                  ? "Removing..."
                  : "Remove admin access"
                : deleting
                  ? "Deleting..."
                  : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
